/**
 * Login Page
 * User authentication
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { track } from '../../../../family-design/analytics'
import { AuthShell, Field, Notice, Spinner, SsoButtons } from './authParts'
import { parseReturnUrl } from '../../lib/returnUrl'

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  google_failed: 'Googleログインに失敗しました。もう一度お試しください。',
  google_cancelled: 'Googleログインがキャンセルされました。',
  google_not_configured: 'Googleログインは現在利用できません。',
  account_disabled: 'このアカウントは現在ご利用いただけません。',
  oauth_state_invalid: 'ログインの確認に失敗しました。もう一度最初からお試しください。',
  invite_required: '現在、新規登録の受付を停止しています。',
  registration_closed: '現在、新規登録の受付を停止しています。',
}

export function LoginPage() {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuth()
  const [searchParams] = useSearchParams()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true,
  })
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [highlightGoogle, setHighlightGoogle] = useState(false)

  useEffect(() => {
    const oauthError = searchParams.get('error')
    if (oauthError && OAUTH_ERROR_MESSAGES[oauthError]) {
      setError(OAUTH_ERROR_MESSAGES[oauthError])
    }
  }, [searchParams])

  // リファラルコード: URL の ?ref= または localStorage から取得して保持
  const ref = searchParams.get('ref') || localStorage.getItem('referralCode') || ''
  useEffect(() => {
    if (ref) localStorage.setItem('referralCode', ref)
  }, [ref])

  // SSO 戻り先: 別 Genemo アプリ（*.genemo.jp・https）からのログインなら成功後にそこへ戻す
  const returnUrl = parseReturnUrl(searchParams.get('return'))
  const oauthQuery = new URLSearchParams()
  if (ref) oauthQuery.set('ref', ref)
  if (returnUrl) oauthQuery.set('return', returnUrl)
  const oauthSuffix = oauthQuery.toString() ? `?${oauthQuery.toString()}` : ''

  // 既に Genemo にログイン済み（共有クッキーあり）で SSO 戻り先がある場合は即座に戻す
  useEffect(() => {
    if (isAuthenticated && returnUrl) {
      window.location.href = returnUrl
    }
  }, [isAuthenticated, returnUrl])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData({ ...formData, [e.target.name]: value })
    setError('')
    setFieldErrors(prev => ({ ...prev, [e.target.name]: undefined }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const errs: { email?: string; password?: string } = {}
    if (!formData.email) errs.email = 'メールアドレスを入力してください'
    if (!formData.password) errs.password = 'パスワードを入力してください'
    setFieldErrors(errs)
    if (errs.email || errs.password) return

    setIsLoading(true)
    setHighlightGoogle(false)
    try {
      await login(formData.email, formData.password, formData.rememberMe)
      track('login', { method: 'email' })
      if (returnUrl) {
        // 別 Genemo アプリからの SSO ログイン → 共有クッキーを持って戻す
        window.location.href = returnUrl
        return
      }
      navigate('/', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'ログインに失敗しました'
      setError(message)
      if (message.includes('Google')) {
        setHighlightGoogle(true)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthShell title="ログイン" foot={<>アカウントは Genemo 共通です。同じメールアドレスで Genemo のすべてのアプリを利用できます。</>}>
      {/* Clarity の録画では入力欄ごと伏せる（components/AnalyticsTracker.tsx） */}
      <form onSubmit={handleSubmit} className="grid gap-4" noValidate data-clarity-mask="true">
        <Field
          type="email"
          id="email"
          label="メールアドレス"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={isLoading}
          autoFocus
          error={fieldErrors.email}
        />
        <Field
          type="password"
          id="password"
          label="パスワード"
          value={formData.password}
          onChange={handleChange}
          autoComplete="current-password"
          disabled={isLoading}
          error={fieldErrors.password}
        />

        {/* Remember me / Forgot password */}
        <div className="flex items-center justify-between text-[13px]">
          <label className="flex min-h-[28px] cursor-pointer items-center gap-2 text-ink">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="h-4 w-4 cursor-pointer accent-primary"
              disabled={isLoading}
            />
            ログイン状態を保持
          </label>
          <Link to="/forgot-password" className="font-bold text-primary hover:underline">
            パスワードを忘れた場合
          </Link>
        </div>

        {error && <Notice tone="bad">{error}</Notice>}

        <button type="submit" disabled={isLoading} className="gm-btn gm-btn-primary gm-btn-block">
          {isLoading ? <Spinner /> : null}
          <span>{isLoading ? 'ログインしています' : 'ログイン'}</span>
        </button>
      </form>

      <SsoButtons googleHref={`/api/auth/google${oauthSuffix}`} rakuripHref={`/api/auth/rakurip${oauthSuffix}`} highlightGoogle={highlightGoogle} />

      <p className="gm-auth-foot">
        初めての方は{' '}
        <Link to={`/signup${oauthSuffix}`}>アカウントを作成</Link>
      </p>
    </AuthShell>
  )
}
